import { get } from 'lodash';
import { Store } from 'redux';
import { useQuery } from 'react-query';
import { Provider } from 'react-redux';
import { createPortal } from 'react-dom';
import useReactRouter from 'use-react-router';
import React, { useEffect, useState, useMemo } from 'react';
import { useQueryString } from '~/react-hooks/use-query-string';
import { useHistory } from 'react-router';

import { Greet } from '~/types/models';
import { GreetStats } from '~/types/api';
import { createRestApi } from '~/App/Api/rest';
import SweetAlertApp from '~/App/Components/SweetAlert/App';
import RecordController from '~/App/Controllers/RecordController';
import {
  setIsFirstTime,
  setGreet,
} from '~/App/Components/Record/Stores/Recorder/Actions';
import { trackGreetEvent } from '~/App/Services/greet';
import { GREET_RECORD_NOW } from '~/generated/AnalyticsTypes';
import useCurrentUser from '~/react-hooks/use-current-user';
import { useGreet } from '~/App/Api/Greets';
import GreetBodyContainer from '../Containers/GreetBodyContainer';
import { TaskCreationAction } from '../Components/TaskCreation/TaskCreationWrapper';
import { openGreetRecorderDrawer } from '../Components/GreetRecorderDrawer';
import { openTaskCreationModal } from '../Components/TaskCreationModal';

export type VideoMessageAction = 'create_task' | TaskCreationAction;

export default function Record() {
  const { action } = useQueryString<{ action?: VideoMessageAction }>();

  const user = useCurrentUser();
  const history = useHistory();

  const { match } = useReactRouter<{ id: string }>();
  const [store, setStore] = useState<Store<Record<string, unknown>>>();

  const { status: greetsStatus, data: greet } = useGreet({
    id: match.params.id,
  });

  const isLoadingGreet = useMemo(
    () => greetsStatus === 'loading',
    [greetsStatus]
  );
  const isCached = useMemo(() => greetsStatus === 'success', [greetsStatus]);

  const statsApi = useMemo(
    () => createRestApi<GreetStats>('/api/v2/stats'),
    []
  );
  const { status: statsStatus, data: stats } = useQuery(
    ['stats', { id: user.id }],
    () => statsApi.get(user.id)
  );

  const isLoadingStats = useMemo(
    () => statsStatus === 'loading',
    [statsStatus]
  );

  const loading = useMemo(
    () => isLoadingGreet && isLoadingStats,
    [isLoadingGreet, isLoadingStats]
  );

  useEffect(() => {
    async function bootstrap() {
      if (!loading) {
        setStore(await new RecordController().record());
      }
    }

    void bootstrap();
  }, [loading]);

  useEffect(() => {
    if (stats && store) {
      store?.dispatch(setIsFirstTime(stats?.total === 0));
    }
  }, [stats, store]);

  useEffect(() => {
    if (store && greet) {
      store.dispatch(setGreet(greet));
    }
  }, [store, greet]);

  useEffect(() => {
    if (action === TaskCreationAction.RecordNow && greet) {
      trackGreetEvent(GREET_RECORD_NOW, greet as Greet);
      openGreetRecorderDrawer(greet);

      history.replace({ search: '' });
    } else if (action === 'create_task') {
      openTaskCreationModal();
    }
  }, [action, greet]);

  if (loading || !store || !isCached || !greet) {
    return <i className='far fa-spinner fa-spin' />;
  }

  return (
    <Provider store={store}>
      <GreetBodyContainer greet={greet} />
      {createPortal(
        <SweetAlertApp
          stateResolver={(state: Record<string, unknown>) =>
            get(state, 'sweetAlert')
          }
        />,
        document.getElementById('sweet-alert-target') as HTMLElement
      )}
    </Provider>
  );
}
